import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, X, Grid, LayoutGrid } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [galleryImages, setGalleryImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [galleryRes, catRes] = await Promise.all([
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/gallery-categories`).catch(() => ({ data: [] })),
      ]);
      
      setGalleryImages(galleryRes.data);
      
      // Build categories array with "all" first
      const apiCategories = catRes.data.map(c => ({ id: c.name, name: c.label }));
      setCategories([{ id: 'all', name: 'Wszystkie' }, ...apiCategories]);
    } catch (error) {
      console.error('Error fetching gallery data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredImages = activeCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse font-heading text-2xl text-forest">Ładowanie galerii...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="gallery-page">
      {/* Header */}
      <header className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-body">Powrót</span>
            </Link>
          </div>
          
          <div className="text-center">
            <span className="font-accent text-2xl text-terracotta">Odkryj nasze miejsce</span>
            <h1 className="font-heading text-4xl md:text-5xl mt-2">Galeria zdjęć</h1>
            <p className="font-body text-white/70 mt-4 max-w-2xl mx-auto">
              Przeglądaj zdjęcia naszych obiektów, wnętrz i atrakcji. 
              Kliknij na zdjęcie, aby zobaczyć je w pełnym rozmiarze.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters & Controls */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 font-body text-sm transition-all ${
                  activeCategory === cat.id
                    ? 'bg-forest text-white'
                    : 'bg-white text-forest hover:bg-forest/10'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* View Mode Toggle */}
          <div className="flex gap-2 bg-white p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-forest text-white' : 'text-forest'}`}
              title="Widok siatki"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('masonry')}
              className={`p-2 ${viewMode === 'masonry' ? 'bg-forest text-white' : 'text-forest'}`}
              title="Widok masonry"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Image Count */}
        <p className="font-body text-forest/60 text-center mb-6">
          Wyświetlono {filteredImages.length} zdjęć
        </p>

        {/* Gallery Grid */}
        {filteredImages.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-body text-forest/60">Brak zdjęć w tej kategorii</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((img, index) => (
              <button
                key={img.id || index}
                onClick={() => openLightbox(index)}
                className="group relative aspect-square overflow-hidden bg-forest/10"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={img.src}
                  alt={img.alt_text || img.title || 'Zdjęcie galerii'}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/40 transition-all duration-300 flex items-center justify-center">
                  <span className="font-body text-white opacity-0 group-hover:opacity-100 transition-opacity text-center px-4">
                    {img.title || 'Zobacz'}
                  </span>
                </div>
                {img.category && (
                  <span className="absolute bottom-2 left-2 bg-white/90 text-forest px-2 py-1 text-xs font-body">
                    {categories.find(c => c.id === img.category)?.name || img.category}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
            {filteredImages.map((img, index) => (
              <button
                key={img.id || index}
                onClick={() => openLightbox(index)}
                className="group relative w-full mb-4 overflow-hidden bg-forest/10 break-inside-avoid"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={img.src}
                  alt={img.alt_text || img.title || 'Zdjęcie galerii'}
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/40 transition-all duration-300 flex items-end">
                  <span className="font-body text-white opacity-0 group-hover:opacity-100 transition-opacity p-4">
                    {img.title || 'Zobacz'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0">
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            {filteredImages[lightboxIndex] && (
              <>
                <img
                  src={filteredImages[lightboxIndex].src}
                  alt={filteredImages[lightboxIndex].alt_text || filteredImages[lightboxIndex].title || 'Zdjęcie galerii'}
                  className="max-w-full max-h-full object-contain"
                />
                
                <button
                  onClick={() => setLightboxOpen(false)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-10"
                >
                  <X className="w-5 h-5 text-forest" />
                </button>

                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-10"
                >
                  <ChevronLeft className="w-6 h-6 text-forest" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center z-10"
                >
                  <ChevronRight className="w-6 h-6 text-forest" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
                  <p className="text-white font-body mb-2">{filteredImages[lightboxIndex].title || 'Zdjęcie'}</p>
                  <p className="bg-black/50 text-white px-4 py-2 rounded-full font-body text-sm">
                    {lightboxIndex + 1} / {filteredImages.length}
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
